import React from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { DateRangePicker } from '../../../../../components/ui/DateRangePicker';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';

/**
 * @param {object} props
 * @param {object} props.filters - The slice's `filters`
 * @param {(patch: object) => void} props.onFilterChange
 * @param {Array<{value: number, label: string}>} [props.companyOptions] - Every
 *   company of the platform, without the "Todas" sentinel, prepended here.
 * @param {Array<{value: number, label: string}>} [props.adminOptions] - The
 *   operators, same shape.
 * @param {Array<{value: number, label: string}>|null} [props.adminCompanyOptions] - The
 *   companies of the operator currently picked, or `null` while there is none.
 * @param {() => void} props.onReload
 * @param {boolean} props.isLoading
 * @param {boolean} props.isStale - Something moved since the last fetch
 */
export const DashboardFilters = ({
  filters,
  onFilterChange,
  companyOptions = [],
  adminOptions = [],
  adminCompanyOptions = null,
  onReload,
  isLoading,
  isStale,
}) => {
  /*
   * Second step of the ladder, and it only exists once the first is answered:
   * the numbers are of the whole platform, then of one operator, then of one of
   * its companies. The catalog the backend sends staff is every company there
   * is, so offering it before an operator is picking from a haystack.
   */
  const hasAdmin = Boolean(adminCompanyOptions);
  const companySelectOptions = hasAdmin
    ? [{ value: 'all', label: 'Todas' }, ...adminCompanyOptions]
    : [];

  return (
    <div className="flex flex-col md:flex-row flex-wrap items-center gap-4">
      <DateRangePicker
        startDate={filters.start_date}
        endDate={filters.end_date}
        onChange={onFilterChange}
      />

      {/* The wider question first: an operator has companies, so picking one
          changes what the next select is about. */}
      <FilterableSelect
        label="Operador:"
        value={filters.admin_id ?? 'all'}
        onChange={(value) => onFilterChange({ admin_id: value })}
        options={[{ value: 'all', label: 'Todos' }, ...adminOptions]}
        searchPlaceholder="Buscar operador..."
      />

      <FilterableSelect
        label="Empresa:"
        value={filters.company}
        onChange={(value) => onFilterChange({ company: value })}
        options={companySelectOptions}
        disabled={!hasAdmin}
        placeholder="Primero elige un operador"
      />

      <div className="flex items-center gap-3 md:ml-auto">
        {/* Only shown when the backend said something moved and the automatic
            refetch has not caught up — a busy account is otherwise refreshing
            on its own and this would blink constantly. */}
        {isStale && !isLoading && (
          <span className="text-xs font-medium text-warning">Hay cambios sin cargar</span>
        )}

        <Button
          variant="secondary"
          size="sm"
          icon={RefreshCw}
          loading={isLoading}
          loadingLabel="Actualizando..."
          onClick={onReload}
        >
          Recargar
        </Button>
      </div>
    </div>
  );
};
